import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const BASEURL = process.env.REACT_APP_API_URL;

const BarcodeVerificationPage = () => {
    const [isScanning, setIsScanning] = useState(false);
    const [currentOrder, setCurrentOrder] = useState(null);
    const [expectedVariants, setExpectedVariants] = useState([]);
    const [scannedVariants, setScannedVariants] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [scanMode, setScanMode] = useState('order'); // 'order' or 'variant'
    const [orderComplete, setOrderComplete] = useState(false);
    const [inputValue, setInputValue] = useState(''); // For manual input
    const [inputError, setInputError] = useState(false); // For red input styling
    
    // Add the missing state variables
    const [barcodeScanning, setBarcodeScanning] = useState({});
    const [isDetecting, setIsDetecting] = useState(false);
    
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const scannerInputRef = useRef(null);
    const navigate = useNavigate();

    const playFeedbackSound = useCallback((type) => {
      try {
        const audio = new Audio(type === 'success' ? '/sounds/success.wav' : '/sounds/error.wav');
        audio.volume = 1;
        audio.currentTime = 0;
        void audio.play().catch((err) => {
          console.error('Sound feedback error:', err);
        });
      } catch (err) {
        console.error('Sound feedback error:', err);
      }
    }, []);

    useEffect(() => {
      scannerInputRef.current?.focus();
    }, [scanMode, currentOrder, orderComplete, isLoading]);

    // Add the missing stopScanning function
    const stopScanning = () => {
        setIsDetecting(false);
        
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        
        setIsScanning(false);
        
        // Also remove any existing scanner modal
        const existingModal = document.getElementById('barcode-scanner-modal');
        if (existingModal) {
            existingModal.remove();
        }
    };

    // Activate keyboard scanner input (hardware barcode reader / Enter key)
    const startScanning = async () => {
      setIsScanning(false);
      setIsDetecting(false);
      setError('');
      setSuccess('');
      setInputError(false);
      scannerInputRef.current?.focus();
      toast.info('Scanner prêt. Saisissez le code-barres puis appuyez sur Entrée.', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    };

    // Replace the startBarcodeDetection function with this exact same logic from VariantsTable
    const startBarcodeScanning = async (scanMode) => {
        setBarcodeScanning(prev => ({ ...prev, [scanMode]: true }));
        
        try {
            // Check if we're in a secure context
            if (!window.isSecureContext) {
                throw new Error('Camera access requires HTTPS or localhost');
            }
            
            // Check if camera is available
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                throw new Error('Camera not supported');
            }

            // Request camera access with mobile-optimized settings
            const stream = await navigator.mediaDevices.getUserMedia({ 
                video: { 
                    facingMode: { ideal: 'environment' },
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                } 
            });
            
            const modalHtml = `
                <div id="barcode-scanner-modal" class="fixed inset-0 z-50 overflow-y-auto">
                    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div class="fixed inset-0 bg-gray-900 bg-opacity-90 transition-opacity"></div>
                        <div class="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            <div class="bg-white px-6 pt-6 pb-4">
                                <div class="flex items-start">
                                    <div class="flex-shrink-0">
                                        <div class="flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                                            <svg class="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"></path>
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                            </svg>
                                        </div>
                                    </div>
                                    <div class="ml-4 w-full">
                                        <h3 class="text-lg leading-6 font-semibold text-gray-900 mb-4">Scanner le code-barres</h3>
                                        <div class="relative">
                                            <video id="barcode-video" class="w-full h-64 bg-black rounded-lg border-2 border-gray-300" autoplay playsinline></video>
                                            <canvas id="barcode-canvas" class="hidden"></canvas>
                                            
                                            <!-- Scanning overlay -->
                                            <div class="absolute inset-0 pointer-events-none">
                                                <div class="absolute inset-4 border-2 border-dashed border-white opacity-50 rounded-lg"></div>
                                                <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                                                    <div class="w-48 h-32 border-2 border-red-500 rounded-lg relative">
                                                        <div class="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-red-500"></div>
                                                        <div class="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-red-500"></div>
                                                        <div class="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-red-500"></div>
                                                        <div class="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-red-500"></div>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div class="absolute bottom-2 left-2 right-2">
                                                <div class="bg-black bg-opacity-70 text-white text-xs px-3 py-2 rounded text-center">
                                                    <div id="scan-status">Pointez la caméra vers le code-barres</div>
                                                    <div class="mt-1 text-yellow-300">Assurez-vous que le code-barres est bien éclairé et net</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row sm:justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                                <button id="capture-btn" type="button" class="w-full sm:w-auto inline-flex justify-center rounded-lg border border-blue-300 shadow-sm px-4 py-2 bg-blue-50 text-sm font-medium text-blue-700 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                                    📸 Capturer
                                </button>
                                <button id="manual-entry-btn" type="button" class="w-full sm:w-auto inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                                    ✏️ Saisie manuelle
                                </button>
                                <button id="close-scanner" type="button" class="w-full sm:w-auto inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-red-600 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
                                    ❌ Fermer
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            `;
            
            document.body.insertAdjacentHTML('beforeend', modalHtml);
            const modal = document.getElementById('barcode-scanner-modal');
            const videoElement = document.getElementById('barcode-video');
            const canvasElement = document.getElementById('barcode-canvas');
            const closeButton = document.getElementById('close-scanner');
            const manualEntryButton = document.getElementById('manual-entry-btn');
            const captureButton = document.getElementById('capture-btn');
            const scanStatus = document.getElementById('scan-status');
            
            videoElement.srcObject = stream;
            videoElement.play();

            let scanInterval = null;
            let isScanningModal = true;

            // Setup canvas for image processing
            const ctx = canvasElement.getContext('2d');

            // Try multiple scanning approaches
            const startScanningProcess = () => {
                scanInterval = setInterval(async () => {
                    if (!isScanningModal) return;

                    try {
                        // Method 1: Try BarcodeDetector API (if available)
                        if ('BarcodeDetector' in window) {
                            try {
                                const barcodeDetector = new window.BarcodeDetector();
                                const barcodes = await barcodeDetector.detect(videoElement);
                                if (barcodes.length > 0) {
                                    const barcode = barcodes[0].rawValue;
                                    onBarcodeDetected(barcode);
                                    return;
                                }
                            } catch (e) {
                                console.log('BarcodeDetector failed, trying alternative methods');
                            }
                        }

                        // Method 2: Canvas-based analysis (simplified pattern detection)
                        if (videoElement.videoWidth > 0 && videoElement.videoHeight > 0) {
                            canvasElement.width = videoElement.videoWidth;
                            canvasElement.height = videoElement.videoHeight;
                            ctx.drawImage(videoElement, 0, 0);
                            
                            // Simple barcode pattern detection (basic implementation)
                            const imageData = ctx.getImageData(0, 0, canvasElement.width, canvasElement.height);
                            const detected = detectBarcodePattern(imageData);
                            if (detected) {
                                scanStatus.textContent = '🔍 Code-barres détecté! Analysing...';
                            }
                        }
                    } catch (error) {
                        console.error('Scanning error:', error);
                    }
                }, 500); // Scan every 500ms for better performance
            };

            // Simple barcode pattern detection
            const detectBarcodePattern = (imageData) => {
                // This is a very basic implementation
                // In a real app, you'd use a proper barcode library like QuaggaJS
                const data = imageData.data;
                let blackPixels = 0;
                let whitePixels = 0;
                
                // Sample pixels to detect bar patterns
                for (let i = 0; i < data.length; i += 16) { // Sample every 4th pixel
                    const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
                    if (avg < 128) blackPixels++;
                    else whitePixels++;
                }
                
                // If we have a good mix of black and white, might be a barcode
                const ratio = blackPixels / (blackPixels + whitePixels);
                return ratio > 0.3 && ratio < 0.7;
            };

            // Function to handle barcode detection
            const onBarcodeDetected = (barcode) => {
                isScanningModal = false;
                clearInterval(scanInterval);
                
                // Process the barcode based on scan mode
                if (scanMode === 'order') {
                    scanOrder(barcode);
                } else {
                    scanVariant(barcode);
                }
                
                // Close modal and stop scanning
                stream.getTracks().forEach(track => track.stop());
                modal.remove();
                setBarcodeScanning(prev => ({ ...prev, [scanMode]: false }));
                setIsScanning(false);
                setIsDetecting(false);
                
                // Show success message
                console.log(`Code-barres scanné: ${barcode}`);
            };

            // Start scanning
            startScanningProcess();

            // Manual capture button - let user manually enter what they see
            captureButton.addEventListener('click', () => {
                const userInput = prompt(
                    scanMode === 'order' 
                        ? 'Veuillez saisir le code de livraison que vous voyez:' 
                        : 'Veuillez saisir le code-barres du produit que vous voyez:'
                );
                if (userInput && userInput.trim()) {
                    onBarcodeDetected(userInput.trim());
                }
            });

            // Close button handler
            closeButton.addEventListener('click', () => {
                isScanningModal = false;
                clearInterval(scanInterval);
                stream.getTracks().forEach(track => track.stop());
                modal.remove();
                setBarcodeScanning(prev => ({ ...prev, [scanMode]: false }));
                setIsScanning(false);
                setIsDetecting(false);
            });

            // Manual entry button handler
            manualEntryButton.addEventListener('click', () => {
                isScanningModal = false;
                clearInterval(scanInterval);
                stream.getTracks().forEach(track => track.stop());
                modal.remove();
                setBarcodeScanning(prev => ({ ...prev, [scanMode]: false }));
                setIsScanning(false);
                setIsDetecting(false);
                
                // Open manual input
                const userInput = prompt(
                    scanMode === 'order' 
                        ? 'Saisissez le code de livraison:' 
                        : 'Saisissez le code-barres du produit:'
                );
                if (userInput && userInput.trim()) {
                    if (scanMode === 'order') {
                        scanOrder(userInput.trim());
                    } else {
                        scanVariant(userInput.trim());
                    }
                }
            });
            
        } catch (error) {
            console.error('Error accessing camera:', error);
            setBarcodeScanning(prev => ({ ...prev, [scanMode]: false }));
            setIsScanning(false);
            setIsDetecting(false);
            
            // Fallback to manual entry
            const userInput = prompt(
                scanMode === 'order' 
                    ? 'Caméra non disponible. Saisissez le code de livraison:' 
                    : 'Caméra non disponible. Saisissez le code-barres du produit:'
            );
            if (userInput && userInput.trim()) {
                if (scanMode === 'order') {
                    scanOrder(userInput.trim());
                } else {
                    scanVariant(userInput.trim());
                }
            }
        }
    };

    // Update the scanOrder function to handle 400 status code
const scanOrder = async (codeLivraison) => {
    setIsLoading(true);
    setError('');

    try {
        const response = await fetch(`${BASEURL}/boncommande/scancodeabarre?CodeLivraison=${encodeURIComponent(codeLivraison)}`);

        if (response.ok) {
            const variantBarcodes = await response.json();
            setCurrentOrder(codeLivraison);
            setExpectedVariants(variantBarcodes);
            setScannedVariants([]);
            setScanMode('variant');
            setOrderComplete(false);
            setInputValue(''); // Clear input
          playFeedbackSound('success');
            setSuccess(`✅ Commande ${codeLivraison} chargée! Scannez maintenant les ${variantBarcodes.length} produits.`);

            // Auto restart scanning for variants
            setTimeout(() => {
                setSuccess('');
                if (!isScanning) {
                    startScanning();
                }
            }, 2000);
        } else if (response.status === 400) {
            setError('❌ Code de livraison invalide. Vérifiez le format du code.');
            setInputError(true); // Make input red
          playFeedbackSound('error');
            toast.error('❌ Code de livraison invalide!', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        } else if (response.status === 404) {
            setError('❌ Commande non trouvée. Vérifiez le code de livraison.');
            setInputError(true); // Make input red
          playFeedbackSound('error');
        } else {
            throw new Error('Erreur lors de la recherche de la commande');
        }
    } catch (err) {
        setError('❌ Erreur de connexion. Veuillez réessayer.');
        playFeedbackSound('error');
        console.error('Order scan error:', err);
    } finally {
        setIsLoading(false);
        setInputValue(''); // Clear input
        
        // Reset input error state after 3 seconds
        setTimeout(() => {
            setInputError(false);
        }, 3000);
    }
};

// Update the scanVariant function to handle invalid barcodes better
const scanVariant = (barcode) => {
    // First check if the barcode format is valid (basic validation)
    if (!barcode || barcode.trim().length < 3) {
        setError('❌ Code-barres invalide! Format trop court.');
        setInputError(true);
        toast.error('❌ Code-barres invalide! Format incorrect.', {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
        });
        setInputValue('');

        setTimeout(() => {
            setError('');
            setInputError(false);
            if (!isScanning) {
                startScanning();
            }
        }, 2000);
        return;
    }

    if (expectedVariants.includes(barcode)) {
        const newScannedVariants = [...scannedVariants, barcode];
        setScannedVariants(newScannedVariants);
        setInputValue(''); // Clear input
        setInputError(false); // Reset input error state

        if (newScannedVariants.length === expectedVariants.length) {
            // Order complete
            setOrderComplete(true);
            setSuccess('🎉 Commande terminée avec succès! Tous les produits ont été scannés.');
          playFeedbackSound('success');
            toast.success('🎉 Commande terminée avec succès!', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            stopScanning();

            // Reset after 3 seconds for next order
            setTimeout(() => {
                resetForNewOrder();
            }, 1200);
        } else {
            // Continue scanning
            setSuccess(`✅ Produit validé! ${newScannedVariants.length}/${expectedVariants.length} scannés.`);
            playFeedbackSound('success');
            toast.success(`✅ Produit validé! ${newScannedVariants.length}/${expectedVariants.length}`, {
                position: "top-right",
                autoClose: 2000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });

            // Clear success message and continue scanning
            setTimeout(() => {
                setSuccess('');
                if (!isScanning) {
                    startScanning();
                }
            }, 1500);
        }
    } else {
        // Wrong barcode - show toast and red input
        setError('❌ Produit incorrect! Ce code-barres ne fait pas partie de cette commande.');
        setInputError(true); // Make input red
    playFeedbackSound('error');
        toast.error('❌ Code-barres invalide pour cette commande!', {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
        });
        setInputValue(''); // Clear input

        setTimeout(() => {
            setError('');
            setInputError(false); // Reset input error state
            if (!isScanning) {
                startScanning();
            }
        }, 2000);
    }
};

    // Reset for new order
    const resetForNewOrder = () => {
        setCurrentOrder(null);
        setExpectedVariants([]);
        setScannedVariants([]);
        setScanMode('order');
        setOrderComplete(false);
        setSuccess('');
        setError('');
        setInputValue(''); // Clear input
        setInputError(false); // Reset input error state
        setTimeout(() => scannerInputRef.current?.focus(), 0);
    };

    // Handle barcode capture from camera
    const captureBarcode = () => {
        const barcode = prompt(
            scanMode === 'order'
                ? 'Saisissez le code de livraison:'
                : 'Saisissez le code-barres du produit:'
        );

        if (barcode) {
            if (scanMode === 'order') {
                scanOrder(barcode);
            } else {
                scanVariant(barcode);
            }
            setIsScanning(false);
        }
    };

    // Handle manual input submission
    const handleManualSubmit = (e) => {
        e.preventDefault();
        if (!inputValue.trim()) return;

        if (scanMode === 'order') {
            scanOrder(inputValue.trim());
        } else {
            scanVariant(inputValue.trim());
        }
        setTimeout(() => scannerInputRef.current?.focus(), 0);
    };

    // Get progress percentage
    const getProgress = () => {
        if (expectedVariants.length === 0) return 0;
        return (scannedVariants.length / expectedVariants.length) * 100;
    };

    return (
        <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section - Added pt-20 to account for navbar */}
      <div className="relative bg-gradient-to-br from-[#174540] via-[#1c5651] to-[#0d2926] overflow-hidden pt-20">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.05%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-50"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-[#83c5be]/20 to-transparent rounded-full blur-lg"></div>
                <div className="relative bg-white/10 backdrop-blur-sm rounded-2xl p-4 ring-1 ring-white/20">
                  <svg className="w-12 h-12 text-[#83c5be]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </div>
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              <span className="block">Vérification</span>
              <span className="block bg-gradient-to-r from-[#83c5be] to-white bg-clip-text text-transparent">
                Commandes
              </span>
            </h1>

            <p className="text-xl text-gray-200 max-w-3xl mx-auto mb-8 leading-relaxed">
              Système professionnel de vérification des commandes par scanning de codes-barres.
              Scannez d'abord la commande, puis vérifiez chaque produit.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Scanner Section */}
          <div className="lg:col-span-2 space-y-8">
            {/* Current Status Card */}
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
              <div className={`px-8 py-6 ${scanMode === 'order'
                    ? 'bg-gradient-to-r from-[#174540] to-[#1c5651]'
                    : orderComplete
                        ? 'bg-gradient-to-r from-green-500 to-green-600'
                        : 'bg-gradient-to-r from-blue-500 to-blue-600'
                  }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-white flex items-center">
                      {scanMode === 'order' ? (
                          <>
                            <svg className="w-7 h-7 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                            Scanner Commande
                          </>
                      ) : orderComplete ? (
                          <>
                            <svg className="w-7 h-7 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Commande Terminée
                          </>
                      ) : (
                          <>
                            <svg className="w-7 h-7 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                            </svg>
                            Scanner Produits
                          </>
                      )}
                    </h2>
                    <p className="text-white/80 mt-1">
                      {scanMode === 'order'
                          ? 'Scannez le code de livraison de la commande'
                          : orderComplete
                              ? 'Tous les produits ont été vérifiés avec succès'
                              : `Commande ${currentOrder} - ${scannedVariants.length}/${expectedVariants.length} produits`
                      }
                    </p>
                  </div>
                  {isScanning && (
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                        <span className="text-green-200 text-sm font-medium">Scan Actif</span>
                      </div>
                  )}
                </div>

                {/* Progress Bar for variants */}
                {scanMode === 'variant' && !orderComplete && (
                    <div className="mt-4">
                      <div className="flex justify-between text-sm text-white/80 mb-2">
                        <span>Progression</span>
                        <span>{scannedVariants.length}/{expectedVariants.length}</span>
                      </div>
                      <div className="w-full bg-white/20 rounded-full h-2">
                        <div
                            className="bg-white h-2 rounded-full transition-all duration-500"
                            style={{ width: `${getProgress()}%` }}
                        ></div>
                      </div>
                    </div>
                )}
              </div>

              <div className="p-8">
                {!isScanning ? (
                    <div className="text-center">
                      <div className="w-40 h-40 mx-auto mb-8 bg-gradient-to-br from-[#174540]/10 via-[#83c5be]/10 to-[#174540]/10 rounded-full flex items-center justify-center relative">
                        <div className="absolute inset-4 border-2 border-dashed border-[#174540]/30 rounded-full animate-pulse"></div>
                        {scanMode === 'order' ? (
                            <svg className="w-20 h-20 text-[#174540]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                        ) : (
                            <svg className="w-20 h-20 text-[#174540]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                            </svg>
                        )}
                      </div>

                      <h3 className="text-2xl font-bold text-gray-900 mb-3">
                        {scanMode === 'order' ? 'Prêt à scanner une commande' : 'Prêt à scanner un produit'}
                      </h3>

                      <p className="text-gray-600 mb-8 text-lg">
                        {scanMode === 'order'
                            ? 'Activez la caméra pour scanner le code de livraison'
                            : `Scannez le prochain produit (${scannedVariants.length}/${expectedVariants.length})`
                        }
                      </p>

                      <div className="flex justify-center mb-8">
                        <button
                            onClick={startScanning}
                            className="group relative inline-flex items-center px-8 py-4 bg-gradient-to-r from-[#174540] to-[#1c5651] text-white font-bold rounded-2xl hover:from-[#0d2926] hover:to-[#174540] transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105"
                        >
                          <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          </svg>
                          <span>Activer le scanner clavier</span>
                        </button>
                      </div>

                      {/* Manual Input Form */}
                      <div className="max-w-md mx-auto">
                        <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center justify-center">
                            <svg className="w-5 h-5 mr-2 text-[#174540]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                            Saisie Manuelle
                          </h4>

                          <form onSubmit={handleManualSubmit} className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                {scanMode === 'order' ? 'Code de livraison' : 'Code-barres produit'}
                              </label>
                              <input
                                  type="text"
                                  ref={scannerInputRef}
                                  value={inputValue}
                                  onChange={(e) => setInputValue(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      handleManualSubmit(e);
                                    }
                                  }}
                                  placeholder={
                                    scanMode === 'order'
                                        ? 'Saisissez le code de livraison...'
                                        : 'Saisissez le code-barres...'
                                  }
                                  className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-[#174540]/20 transition-all duration-200 text-center font-mono text-lg ${inputError
                                      ? 'border-red-500 bg-red-50 focus:border-red-500 focus:ring-red-500/20 animate-shake'
                                      : 'border-gray-200 focus:border-[#174540]'
                                    }`}
                                  autoFocus
                                  disabled={isLoading}
                              />
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading || !inputValue.trim()}
                                className="w-full bg-gradient-to-r from-[#83c5be] to-[#6ba3a0] text-white py-3 px-6 rounded-xl font-semibold hover:from-[#6ba3a0] hover:to-[#5a8b88] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                            >
                              {isLoading ? (
                                  <div className="flex items-center justify-center">
                                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Vérification...
                                  </div>
                              ) : (
                                  <>
                                    <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    {scanMode === 'order' ? 'Charger Commande' : 'Vérifier Produit'}
                                  </>
                              )}
                            </button>
                          </form>
                        </div>
                      </div>
                    </div>
                ) : (
                    <div className="space-y-6">
                      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                        <div className="flex items-center justify-between gap-4 flex-wrap">
                          <div>
                            <h3 className="text-2xl font-bold text-gray-900">
                              {scanMode === 'order' ? 'Scanner la commande' : 'Scanner les produits'}
                            </h3>
                            <p className="mt-1 text-gray-600">
                              Utilisez le lecteur code-barres ou le clavier, puis appuyez sur Entrée.
                            </p>
                          </div>
                          <div className={`px-3 py-2 rounded-full text-xs font-medium ${scanMode === 'order' ? 'bg-[#174540]/10 text-[#174540]' : 'bg-blue-100 text-blue-700'}`}>
                            {scanMode === 'order' ? 'Mode commande' : 'Mode produit'}
                          </div>
                        </div>

                        <div className="mt-6 bg-gray-50 rounded-2xl p-4 border border-gray-200">
                          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center justify-center">
                            <svg className="w-5 h-5 mr-2 text-[#174540]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                            Saisie / lecteur code-barres
                          </h4>

                          <form onSubmit={handleManualSubmit} className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                {scanMode === 'order' ? 'Code de livraison' : 'Code-barres produit'}
                              </label>
                              <input
                                ref={scannerInputRef}
                                type="text"
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleManualSubmit(e);
                                  }
                                }}
                                placeholder={scanMode === 'order' ? 'Scannez le code de livraison puis Entrée' : 'Scannez le produit puis Entrée'}
                                className={`w-full px-4 py-4 border-2 rounded-xl focus:ring-2 focus:ring-[#174540]/20 transition-all duration-200 text-center font-mono text-xl ${inputError ? 'border-red-500 bg-red-50 focus:border-red-500 focus:ring-red-500/20 animate-shake' : 'border-gray-200 focus:border-[#174540]'}`}
                                autoFocus
                                disabled={isLoading}
                              />
                            </div>

                            <button
                              type="submit"
                              disabled={isLoading || !inputValue.trim()}
                              className="w-full bg-gradient-to-r from-[#83c5be] to-[#6ba3a0] text-white py-3 px-6 rounded-xl font-semibold hover:from-[#6ba3a0] hover:to-[#5a8b88] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                            >
                              {isLoading ? 'Vérification...' : (scanMode === 'order' ? 'Charger Commande' : 'Vérifier Produit')}
                            </button>
                          </form>
                        </div>
                      </div>
                    </div>
                )}
              </div>
            </div>

            {/* Status Messages */}
            {(success || error || isLoading) && (
                <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
                  <div className="p-8">
                    {isLoading && (
                        <div className="flex items-center justify-center py-8">
                          <svg className="animate-spin -ml-1 mr-3 h-8 w-8 text-[#174540]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span className="text-xl font-medium text-[#174540]">
                            {scanMode === 'order' ? 'Chargement de la commande...' : 'Vérification du produit...'}
                          </span>
                        </div>
                    )}

                    {success && (
                        <div className="text-center py-6">
                          <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <h3 className="text-xl font-bold text-green-600 mb-2">{success}</h3>
                        </div>
                    )}

                    {error && (
                        <div className="text-center py-6">
                          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </div>
                          <h3 className="text-xl font-bold text-red-600 mb-2">{error}</h3>
                        </div>
                    )}
                  </div>
                </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Order Status Card */}
            {currentOrder && (
                <div className="bg-white rounded-3xl shadow-2xl p-6 border border-gray-100">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <svg className="w-6 h-6 mr-2 text-[#174540]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Commande Actuelle
                  </h3>

                  <div className="bg-[#174540]/5 border border-[#174540]/20 rounded-xl p-4 mb-4">
                    <p className="text-sm text-[#174540] font-medium">Code de Livraison</p>
                    <p className="text-2xl font-bold text-[#174540]">{currentOrder}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                      <p className="text-xs text-blue-600 font-medium">Total Produits</p>
                      <p className="text-xl font-bold text-blue-700">{expectedVariants.length}</p>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                      <p className="text-xs text-green-600 font-medium">Scannés</p>
                      <p className="text-xl font-bold text-green-700">{scannedVariants.length}</p>
                    </div>
                  </div>

                  {orderComplete && (
                      <div className="mt-4 bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                        <svg className="w-8 h-8 text-green-600 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-green-600 font-bold">Commande Validée!</p>
                      </div>
                  )}
                </div>
            )}

            {/* Instructions Card */}
            <div className="bg-gradient-to-br from-[#174540] to-[#1c5651] rounded-3xl shadow-2xl p-6 text-white">
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Instructions
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start space-x-3">
                  <span className="bg-[#83c5be] text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">1</span>
                  <p className="text-[#83c5be]">Scannez ou saisissez le code de livraison de la commande</p>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="bg-[#83c5be] text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">2</span>
                  <p className="text-[#83c5be]">Scannez ou saisissez chaque code-barres produit</p>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="bg-[#83c5be] text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">3</span>
                  <p className="text-[#83c5be]">Validez la commande complète automatiquement</p>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-white/20">
                <h4 className="font-semibold mb-2 text-[#83c5be]">Modes d'utilisation :</h4>
                <div className="space-y-2 text-xs text-white/80">
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-[#83c5be]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    </svg>
                    <span>Scanner avec caméra</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-[#83c5be]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    <span>Saisie manuelle</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      <style jsx>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }
        
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </div>
    );
};

export default BarcodeVerificationPage;